import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Plus, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { PickerSearch, matchesQuery } from "@/components/admin/picker-search";
import { GiveAccessDialog, type AccessKind } from "@/components/toolbox/give-access-dialog";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/lib/date-only";
import { todayLocal } from "@/lib/toolbox";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/toolbox/access")({
  component: CrewAccessPage,
});

type State = "scheduled" | "active" | "ended" | "revoked";

interface Delegation {
  id: string;
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  projectFinished: boolean;
  expiredOn: string | null;
  startDate: string;
  endDate: string | null;
  note: string | null;
  createdByName: string | null;
  state: State;
}

const STATE: Record<State, { label: string; cls: string }> = {
  active: { label: "Active", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  scheduled: { label: "Starts later", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  ended: { label: "Ended", cls: "bg-slate-500/15 text-slate-700 dark:text-slate-300" },
  revoked: { label: "Taken back", cls: "bg-slate-500/15 text-slate-700 dark:text-slate-300" },
};

/** One person's access on one project. */
function AccessCard({ d, onEdit, onTakeBack, busy }: { d: Delegation; onEdit: (d: Delegation) => void; onTakeBack: (d: Delegation) => void; busy: boolean }) {
  const live = d.state === "active" || d.state === "scheduled";
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-2 px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{d.userName}</p>
          <p className="text-xs text-muted-foreground">{d.projectName}</p>
          <p className="text-xs text-muted-foreground">{period(d)}{d.createdByName ? ` · by ${d.createdByName}` : ""}</p>
          {!live && d.expiredOn && <p className="text-xs text-muted-foreground">Expired {formatDateOnly(d.expiredOn)}{d.projectFinished ? " (project finished)" : ""}</p>}
          {d.note && <p className="mt-1 whitespace-pre-line text-xs">{d.note}</p>}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATE[d.state].cls)}>{STATE[d.state].label}</span>
          {!d.projectFinished && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => onEdit(d)}>Edit</Button>}
          {live && <Button size="sm" variant="ghost" className="h-8 text-xs text-destructive" disabled={busy} onClick={() => onTakeBack(d)}>Take back</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

function period(d: Delegation) {
  return d.endDate ? `${formatDateOnly(d.startDate)} to ${formatDateOnly(d.endDate)}` : `From ${formatDateOnly(d.startDate)} until the project is finished`;
}

/** Admins, supervisors and foremen: who on the crew may run toolbox meetings on a project, and for how long. */
function CrewAccessPage() {
  const goBack = useGoBack({ to: "/toolbox" });
  const qc = useQueryClient();
  const [showExpired, setShowExpired] = useState(false);
  const [kind, setKind] = useState<AccessKind>("toolbox");
  const [giving, setGiving] = useState(false);
  const [editing, setEditing] = useState<Delegation | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["delegations", kind],
    queryFn: () => apiGet<{ data: Delegation[]; canGrant: boolean; noProjects?: boolean }>(`/api/delegations?kind=${kind}`),
  });
  const takeBack = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/delegations/${id}`),
    onSuccess: () => {
      toast.success("Access taken back");
      qc.invalidateQueries({ queryKey: ["delegations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = data?.data ?? [];
  const current = rows.filter((r) => r.state === "active" || r.state === "scheduled");
  const expired = rows.filter((r) => r.state === "ended" || r.state === "revoked");

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-10">
      <div className="flex items-center justify-between gap-2 pt-2">
        <div className="flex items-center gap-2">
          <button type="button" onClick={goBack} className="rounded p-1 hover:bg-muted" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
          <h1 className="text-xl font-bold text-text-primary">Crew access</h1>
        </div>
        {data?.canGrant && <Button size="sm" className="gap-1" onClick={() => setGiving(true)}><Plus className="h-4 w-4" /> Give access</Button>}
      </div>
      <div className="flex gap-1 rounded-lg border bg-muted/30 p-1 text-sm">
        {([["toolbox", "Toolbox"], ["flha", "FLHA"]] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setKind(k)} className={cn("flex-1 rounded-md px-3 py-1.5 font-medium", kind === k ? "bg-background shadow-sm" : "text-muted-foreground")}>{label}</button>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        {kind === "toolbox"
          ? "Let crew run toolbox meetings on one project for a set time, or until the project is finished. They can write up a toolbox, use the library and collect signatures there, and nowhere else."
          : "Let crew fill in FLHAs on one project for a set time, or until the project is finished. You can also give access from the toolbox or FLHA page while you are working on it."}
      </p>

      {isLoading && <Skeleton className="h-24 w-full rounded-xl" />}
      {isError && (
        <Card>
          <CardContent className="space-y-2 py-6 text-center">
            <p className="text-sm text-destructive">Crew access could not be loaded.</p>
            <p className="text-xs text-muted-foreground">The app may still be updating. Try again in a few minutes; if it keeps failing, tell the app admin.</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Try again</Button>
          </CardContent>
        </Card>
      )}
      {data && !data.canGrant && <p className="text-sm text-muted-foreground">Only admins, supervisors and project foremen can give this access.</p>}
      {data?.canGrant && data.noProjects && (
        <Card>
          <CardContent className="space-y-1 py-6 text-center">
            <p className="text-sm font-medium">There is no active project to give access on yet.</p>
            <p className="text-xs text-muted-foreground">Super admins can use any active project, division admins and supervisors the projects of their divisions, and foremen their own projects. Create or activate a project first.</p>
          </CardContent>
        </Card>
      )}

      {data?.canGrant && (
        <>
          {current.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center py-12 text-center">
                <UserCheck className="mb-2 h-10 w-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Nobody has been given access right now.</p>
              </CardContent>
            </Card>
          )}
          {current.map((d) => (
            <AccessCard key={d.id} d={d} onEdit={setEditing} onTakeBack={(x) => window.confirm(`Take back ${x.userName}'s access at ${x.projectName}?`) && takeBack.mutate(x.id)} busy={takeBack.isPending} />
          ))}

          {/* Expired access stays here for a month, so it can be edited and made valid again */}
          {expired.length > 0 && (
            <div className="pt-2">
              <button type="button" onClick={() => setShowExpired((v) => !v)} className="flex w-full items-center justify-between rounded-lg border bg-muted/30 px-3 py-2 text-sm font-medium" aria-expanded={showExpired}>
                <span>Expired ({expired.length})</span>
                <ChevronDown className={cn("h-4 w-4 transition-transform", showExpired && "rotate-180")} />
              </button>
              {showExpired && (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-muted-foreground">Kept for one month after it expired, then deleted. Press Edit to make one valid again.</p>
                  {expired.map((d) => (
                    <AccessCard key={d.id} d={d} onEdit={setEditing} onTakeBack={() => {}} busy={false} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <GiveAccessDialog open={giving} onClose={() => setGiving(false)} kind={kind} />
      <EditAccessDialog item={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

/** Change the dates and note of access that was given. The person and the project stay; to change them, take it back and give it again. */
function EditAccessDialog({ item, onClose }: { item: Delegation | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [loaded, setLoaded] = useState<string | null>(null);
  if (item && loaded !== item.id) {
    setLoaded(item.id);
    setStart(item.startDate);
    setEnd(item.endDate ?? "");
    setNote(item.note ?? "");
  }
  const close = () => {
    setLoaded(null);
    onClose();
  };
  const save = useMutation({
    mutationFn: () => apiPatch(`/api/delegations/${item!.id}`, { startDate: start, endDate: end || null, note }),
    onSuccess: () => {
      toast.success("Access changed. The person was told.");
      qc.invalidateQueries({ queryKey: ["delegations"] });
      close();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogTitle>{item && (item.state === "ended" || item.state === "revoked") ? "Make access valid again" : "Change access"}</DialogTitle>
        <DialogDescription>{item?.userName} at {item?.projectName}. They are told about the change.{item?.state === "ended" ? " Set an end date that has not passed, or clear it to run until the project is finished." : ""}</DialogDescription>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ea-start">From</Label>
              <Input id="ea-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 text-base md:text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ea-end">Until (optional)</Label>
              <Input id="ea-end" type="date" min={start} value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 text-base md:text-sm" />
            </div>
          </div>
          <p className="-mt-1 text-xs text-muted-foreground">{end ? "Access stops after the end date." : "No end date: access lasts until the project is finished."}</p>
          <div className="space-y-1.5">
            <Label htmlFor="ea-note">Note</Label>
            <Textarea id="ea-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={close}>Cancel</Button>
          <Button type="button" disabled={!start || save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving..." : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
